import QueryFetchStatePage from "@components/QueryFetchStatePage";
import { gzLog, initializeAnalyticsWithDeviceId } from "@hooks/useGzLog";
import { UserSubscriptionsProvider } from "@hooks/useUserSubscriptions";
import CompleteSignUpPage from "@pages/complete_signup";
import { authClient, AuthSession } from "@utils/authClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useUserProfileQuery } from "@utils/queries";
import { disableAuthRoutes } from "@utils/routeUtils";
import { supabase } from "@utils/serverClient";
import { usePathname } from "next/navigation";
import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { CustomOrg } from "types";
import useDialog from "../useDialog";
import { AuthReturnProps, Profile, UpdateProfileProps, UserProfile } from "./types";

const authContext = createContext<AuthReturnProps | null>(null);
const { Provider } = authContext;
type Props = {
  children: React.ReactNode;
};
export function AuthProvider({ children }: Props) {
  const [userSession, setuserSession] = useState<AuthSession | null | undefined>(
    undefined,
  );

  const { showErrorSnackbar } = useDialog();
  const pathname = usePathname();

  const [mounted, setMounted] = useState(false);
  const userSessionRef = useRef<AuthSession | null | undefined>(undefined);

  const _queryKey = useMemo(
    () => ["user", userSession?.user.id],
    [userSession],
  );

  useEffect(() => {
    async function fetchUser() {
      const session = await authClient.initialize();

      userSessionRef.current = session;
      setuserSession(session);
      setMounted(true);
    }

    fetchUser();
  }, []);

  const queryClient = useQueryClient();

  const [selectedOrg, _setSelectedOrg] = useState<CustomOrg>();
  function setSelectedOrg(org?: CustomOrg) {
    if (_userData?.user?.id) {
      if (org) {
        localStorage.setItem(
          "selectedOrg_" + _userData?.user?.id,
          JSON.stringify({
            id: org.id,
            name: org.name,
            deleted: false,
          }),
        );
      } else {
        localStorage.removeItem("selectedOrg_" + _userData?.user?.id);
      }
    }
    _setSelectedOrg(org);
  }

  const {
    data: userData,
    status: profileFetchStatus,
    refetch: getProfile,
  } = useUserProfileQuery(userSession);

  useEffect(() => {
    if (!mounted) return;

    const { data: { subscription } } = authClient.onAuthStateChange((_event, ses) => {
      initializeAnalyticsWithDeviceId(btoa(ses?.user?.id ?? "anon"));
      if (_event !== "SIGNED_IN") {
        if (userSessionRef.current?.access_token !== ses?.access_token) {
          userSessionRef.current = ses;
          setuserSession(ses);
        }
      } else {
        if (ses) {
          gzLog({
            name: "UserRefreshed",
            eventtype: "Auth",
            payload: {
              user: ses.user,
            },
          });
        } else {
          gzLog({
            name: "LoggedOut",
            eventtype: "Auth",
            payload: {
              userId: userSessionRef.current?.user?.id,
            },
          });
        }
        userSessionRef.current = ses;
        setuserSession(ses);
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [mounted]);

  function signOut() {
    setSelectedOrg()
    authClient.signOut();
  }

  const { mutate: updateProfile, status: profileUpdateStatus } = useMutation({
    mutationFn: async (props: UpdateProfileProps) => {
      const _user = userSession?.user;

      if (!_user) throw new Error("User not found");

      const updates = {
        id: _user.id,
        email: _user.email,
        updated_at: new Date().toISOString(),
        org_id: props.updatedUser.org_id,
      };

      const { error } = await supabase.from("profile").upsert(updates);

      if (error) {
        throw error;
      } else {
        return updates;
      }
    },

    onError(error) {
      showErrorSnackbar("Something Went wrong!");
      gzLog({
        eventtype: "UserProfileUpdateFailed",
        name: "UserProfileUpdateFailed",
        payload: {
          userId: userSession?.user.id,
          organizationId: userSession?.user.user_metadata.org_id,
          error: error.message,
        },
      });
    },
    onSuccess(data) {
      gzLog({
        eventtype: "UserProfileUpdated",
        name: "UserProfileUpdated",
        payload: {
          userId: data.id,
          organizationId: data.org_id ?? "undefined",
          updatedFields: Object.keys(data).filter(key => key !== 'id' && key !== 'updated_at' && key !== 'email'),
        },
      });
    },
    onSettled(_, __, { notify = true }) {
      if (!notify) return;
      queryClient.invalidateQueries({ queryKey: _queryKey });
      getProfile();
    },
  });

  function setUser(prof?: Profile | null) {
    queryClient.setQueryData(_queryKey, () => {
      if (prof === null || prof === undefined)
        return {
          status: "unauthenticated",
          user: null,
        };

      return {
        status: "authenticated",
        user: prof,
      };
    });
  }

  function setUpdatedUser(prof: Profile) {
    setUser(prof);
  }

  const _userData = userData as any
  const user: UserProfile = userData == null || _userData?.user === null
    ? undefined
    : _userData.user

  useEffect(() => {
    if (_userData?.user?.organizations?.length) {
      const selectedOrgLocal = localStorage.getItem(
        "selectedOrg_" + _userData?.user?.id,
      );
      if (selectedOrgLocal) {
        try {
          const parsed = JSON.parse(selectedOrgLocal);
          if ("id" in parsed && parsed.id) {
            const orgId = parsed.id;
            if (!orgId) return;
            const allOrgsWithId = user?.organizations.filter(o => o.id === orgId)
            const org = allOrgsWithId?.find(o => o.profile?.id === user.id) ?? allOrgsWithId?.[0]
            if (org) _setSelectedOrg(org);
            else _setSelectedOrg(user?.organizations?.find((o: any) => o.profile?.id === user.id) ?? user?.organizations?.[0]);
          } else throw new Error("Invalid Org");
        } catch (e) {
          _setSelectedOrg(user?.organizations?.find((o: any) => o.profile?.id === user.id) ?? user?.organizations?.[0]);
        }
      } else _setSelectedOrg(user?.organizations?.find((o: any) => o.profile?.id === user.id) ?? user?.organizations?.[0]);
    }
  }, [userData, user]);

  const organization = selectedOrg ?? user?.organizations?.find((o: any) => o.profile?.id === user.id) ?? user?.organizations?.[0]

  return (
    <Provider
      value={{
        session: userSession ?? null,
        profileFetchStatus,
        profileUpdateStatus,
        user,
        updateProfile,
        signOut,
        setUpdatedUser,
        getProfile,
        organization,
        setSelectedOrg,
      }}
    >
      {disableAuthRoutes.includes(pathname ?? "") ? (
        <>{children}</>
      ) : (
        <div className="h-screen w-screen">
          <QueryFetchStatePage
            queryFetchStatus={profileFetchStatus}
            onRetry={getProfile}
          >
            {userData && (
              <>
                {!_userData?.user ? (
                  <>{children}</>
                ) : !organization ? (
                  <CompleteSignUpPage />
                ) : organization && (
                  <UserSubscriptionsProvider
                    organization={organization}
                  >
                    {children}
                  </UserSubscriptionsProvider>
                )}
              </>
            )}
          </QueryFetchStatePage>
        </div>
      )}
    </Provider>
  );
}

export const useAuth: () => AuthReturnProps = () => {
  const context = useContext(authContext);
  if (!context) throw "useAuth must be used within a AuthProvider";
  return context;
};
